/* IPRSuggestions — iter98.74 (smart IPR autopilot).
 *
 * Reads the planner's authored `tooth_movements_plan` and surfaces
 * one-click IPR suggestions next to the IPR picker:
 *
 *   "Tooth 14 is moving 1.5mm distally — add 0.3mm IPR at 14-15?"
 *
 * Rules:
 *   - Only suggest for movements where any direction ∈ {mesial, distal}
 *     AND amount_mm > 1mm. (Below 1mm the clinical guidance is no IPR
 *     needed — natural strain absorbs it.)
 *   - Suggested amount defaults to 0.3mm at stage 1 (planner can edit).
 *   - Skip pairs already present in `plan.ipr_entries` (dedupe).
 *   - Skip pairs that cross the midline (11-21 / 31-41) — those
 *     contacts are anatomically risky for IPR.
 *
 * Output: clicking "Add" appends a new IPR row via the picker's
 * onChange. The planner can then tweak amount/stage in the picker UI.
 *
 * Empty state: when there are no qualifying movements or all
 * suggestions are already in the picker, the component renders nothing.
 */
import React, { useMemo } from "react";
import { Sparkle, Plus } from "@phosphor-icons/react";


/** FDI adjacency. Returns the adjacent tooth code for `tooth` in the
 *  given direction, or null when:
 *    - tooth is invalid / not in the permanent FDI set, or
 *    - the adjacency would cross the midline (returns null for "mesial"
 *      on a central incisor — we skip those suggestions per clinical
 *      guidance: midline contacts are anatomically risky for IPR).
 */
function adjacentToothFDI(tooth, direction) {
    const t = Number(tooth);
    if (!Number.isInteger(t)) return null;
    const q = Math.floor(t / 10);    // quadrant 1..4
    const p = t % 10;                 // position 1..8
    if (q < 1 || q > 4 || p < 1 || p > 8) return null;
    if (direction === "mesial") {
        if (p === 1) return null;     // skip midline-crossing pairs
        return q * 10 + (p - 1);
    }
    if (direction === "distal") {
        if (p >= 8) return null;
        return q * 10 + (p + 1);
    }
    return null;
}


/** Conventional notation: lower number first ("13-14" not "14-13"). */
function iprPairLabel(a, b) {
    const lo = Math.min(a, b), hi = Math.max(a, b);
    return `${lo}-${hi}`;
}


function buildSuggestions(toothMovementsPlan, iprEntries) {
    const existing = new Set(
        (iprEntries || []).map((r) => (r?.tooth_pair || "").trim()).filter(Boolean)
    );
    const out = [];
    const seen = new Set();
    for (const mv of toothMovementsPlan || []) {
        const tooth = Number(mv?.tooth);
        const amt = Number(mv?.amount_mm);
        if (!Number.isInteger(tooth) || !(amt > 1.0)) continue;
        const directions = mv?.directions || [];
        for (const dir of directions) {
            if (dir !== "mesial" && dir !== "distal") continue;
            const adj = adjacentToothFDI(tooth, dir);
            if (!adj) continue;
            const pair = iprPairLabel(tooth, adj);
            if (existing.has(pair) || seen.has(pair)) continue;
            seen.add(pair);
            out.push({
                pair,
                tooth,
                direction: dir,
                amount_mm: 0.3,            // default — planner can tweak
                stage: 1,
                rationale: `Tooth ${tooth} → ${amt.toFixed(1)}mm ${dir}`,
            });
        }
    }
    return out;
}


export default function IPRSuggestions({
    toothMovementsPlan,
    iprEntries,
    onAdd,
    onAddAll,
    readOnly = false,
}) {
    const suggestions = useMemo(
        () => buildSuggestions(toothMovementsPlan, iprEntries),
        [toothMovementsPlan, iprEntries]
    );

    if (readOnly || suggestions.length === 0) return null;

    const handleAdd = (s) => {
        if (!onAdd) return;
        onAdd({
            tooth_pair: s.pair,
            amount_mm: s.amount_mm,
            stage: s.stage,
            note: `Auto-suggested for ${s.rationale}`,
        });
    };

    const handleAddAll = () => {
        if (onAddAll) {
            onAddAll(
                suggestions.map((s) => ({
                    tooth_pair: s.pair,
                    amount_mm: s.amount_mm,
                    stage: s.stage,
                    note: `Auto-suggested for ${s.rationale}`,
                }))
            );
        } else if (onAdd) {
            suggestions.forEach(handleAdd);
        }
    };

    return (
        <div
            className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2"
            data-testid="ipr-suggestions"
        >
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Sparkle size={14} weight="fill" className="text-primary" />
                    <span className="text-[11px] font-bold tracking-[0.12em] uppercase text-primary">
                        Smart IPR suggestions
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                        · {suggestions.length} based on per-tooth movements &gt; 1mm
                    </span>
                </div>
                <button
                    type="button"
                    onClick={handleAddAll}
                    className="text-[11px] font-semibold text-primary hover:underline"
                    data-testid="ipr-suggestions-add-all"
                >
                    Add all
                </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
                {suggestions.map((s) => (
                    <button
                        key={s.pair}
                        type="button"
                        onClick={() => handleAdd(s)}
                        data-testid={`ipr-suggestion-${s.pair}`}
                        className="flex items-center justify-between gap-2 text-left bg-card border border-border rounded-md px-2.5 py-1.5 hover:border-primary/50 hover:bg-primary/5 transition-colors"
                    >
                        <span className="flex flex-col">
                            <span className="font-mono text-xs font-bold">
                                {s.pair} · 0.3 mm @ stage 1
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                                {s.rationale}
                            </span>
                        </span>
                        <Plus size={12} weight="bold" className="text-primary shrink-0" />
                    </button>
                ))}
            </div>
        </div>
    );
}


/* Test hook: expose internals so unit tests can verify adjacency math
 * without rendering React. Intentionally not part of the public API. */
export const __TEST__ = { adjacentToothFDI, iprPairLabel, buildSuggestions };
